/* eslint-disable no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import { MapboxLayer } from '@deck.gl/mapbox';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { Deck } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import turf from '@turf/circle';
import { checkState } from '../../utils/functions';
import {
  handleRegionSelect,
  fetchData,
  handleSetCoordinates,
  handleAddress,
} from '../../state/actions/createData';

const MAPBOX_TOKEN =
  'pk.eyJ1Ijoic2Jzb25saW5lIiwiYSI6IklRMkRCVVEifQ.ldQz-kg5lTylBG6J4HoXbg';

export default function Map() {
  const mapContainer = useRef();
  /*
  const popup = useRef(
    new mapboxgl.Popup({
      className: 'elec-popup',
      closeButton: false,
      closeOnClick: true,
    }),
  );
  */

  const geoPP = useSelector((state) => state.data.geoPP);
  const coords = useSelector((state) => state.data.coords);
  const region = useSelector((state) => state.data.region);
  const radius = useSelector((state) => state.data.circRadius);
  const dispatch = useDispatch();

  mapboxgl.accessToken = MAPBOX_TOKEN;

  // internal state
  const [map, setMap] = useState();
  const [deck, setDeck] = useState();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [geoFlyTo, setGeoFlyTo] = useState(false);

  // const ringColour = [255, 150, 12];

  console.log('geoFlyTo ', geoFlyTo);

  // init zoom
  function initZoom() {
    return window.innerWidth < 500 ? 9 : window.innerWidth < 1000 ? 10 : 11;
  }

  // geocoder - enter your information
  const geocoder = new MapboxGeocoder({
    accessToken: mapboxgl.accessToken,
    placeholder: 'Enter address',
    countries: 'au',
    marker: false,
    flyTo: false,
    minLength: 5,
    mapboxgl,
  });

  // use effect for inital
  useEffect(() => {
    const initializeMap = ({ setMap, mapContainer }) => {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/sbsonline/cl2fcwepi000115od7t3saa69',
        // style: 'mapbox://styles/sbsonline/cl14gqql4000q14mulucx1lz8',
        center: [151.1068, -33.873],
        zoom: initZoom(),
        maxZoom: 16,
        pitch: 45,
        bearing: 0,
      });

      // deck layer
      const deck = new Deck({
        gl: map.painter.context.gl,
        layers: [
          new GeoJsonLayer({
            id: 'geojsonLayer',
            type: GeoJsonLayer,
            data: {
              type: 'FeatureCollection',
              features: [],
            },
            opacity: 0.8,
            stroked: false,
            filled: true,
            extruded: true,
            wireframe: true,
            getElevation: (f) => {
              return f.properties.votes * 100;
            },
            getFillColor: [0, 0, 0],
            getLineColor: [0, 0, 0],
            pickable: false,
          }),
        ],
      });

      map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
        }),
        'top-left',
      );
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
      map.scrollZoom.disable();

      map.on('load', () => {
        setMap(map);
        setDeck(deck);
        setMapLoaded(true);
      });
    };
    if (!map) initializeMap({ setMap, mapContainer });
  }, [map]);

  // add Deck.gl layer to mapbox
  useEffect(() => {
    if (mapLoaded) {
      map.addLayer(
        new MapboxLayer({ id: 'geojsonLayer', deck }),
        'waterway-label',
      );
    }
  }, [mapLoaded]);

  // search addreess
  // add geo controls & functions
  function onFlyStart() {
    setGeoFlyTo(true);
  }

  function onFlyEnd() {
    setGeoFlyTo(false);
  }

  const onGeocoder = (e) => {
    console.log('e ', e);
    if (e?.result?.place_name) {
      // check region
      const geoRegion = checkState(e.result.place_name);
      console.log('region', region, geoRegion);
      if (geoRegion !== '') {
        // send coordintaes to state
        dispatch(handleSetCoordinates(e.result.center));
        dispatch(handleAddress(e.result.place_name));
        dispatch(handleRegionSelect(geoRegion));
        dispatch(fetchData(true));
      }
    }
  };

  useEffect(() => {
    if (mapLoaded) {
      map.addControl(geocoder);
      // when a geolocate event occurs.
      geocoder.on('result', onGeocoder);
    }

    return () => {
      if (mapLoaded) {
        geocoder.off('result', onGeocoder);
      }
    };
  }, [mapLoaded]);

  // update geojson
  useEffect(() => {
    if (mapLoaded && geoPP.features.length > 0) {
      deck.setProps({
        layers: [
          new GeoJsonLayer({
            id: 'geojsonLayer',
            type: GeoJsonLayer,
            data: geoPP,
            opacity: 0.8,
            stroked: false,
            filled: true,
            extruded: true,
            wireframe: true,
            getElevation: (f) => {
              return f.properties.votes * 0.25;
            },
            getFillColor: [0, 0, 0],
            getLineColor: [0, 0, 0],
            pickable: false,
          }),
        ],
      });
    }
  }, [mapLoaded, geoPP]);

  // update circle
  useEffect(() => {
    if (mapLoaded && coords.length !== 0) {
      map.on('movestart', onFlyStart);
      map.on('moveend', onFlyEnd);

      map.flyTo({
        center: coords,
        zoom: 11,
      });

      const showCirc = (center) => {
        if (map.getLayer('circle-line')) {
          map.removeLayer('circle-line');
        }
        /*
        if (map.getLayer('circle-line-mini')) {
          map.removeLayer('circle-line-mini');
        } */

        if (map.getLayer('circle-fill')) {
          map.removeLayer('circle-fill');
        }
        if (map.getSource('circle-src')) {
          map.removeSource('circle-src');
        }
        /*
        if (map.getSource('circle-src-mini')) {
          map.removeSource('circle-src-mini');
        } */

        const options = {
          steps: 128,
          units: 'kilometers',
        };
        const circle = turf(center, radius, options);
        // const circleMini = turf(center, 0.4, options);

        map.addSource('circle-src', {
          type: 'geojson',
          data: circle,
        });

        /* 
        map.addSource('circle-src-mini', {
          type: 'geojson',
          data: circleMini,
        }); */

        map.addLayer(
          {
            id: 'circle-fill',
            source: 'circle-src',
            type: 'fill',
            paint: {
              'fill-color': '#000000',
              'fill-opacity': 0.2,
            },
          },
          'border-national-dispute',
        );

        map.addLayer(
          {
            id: 'circle-line',
            source: 'circle-src',
            type: 'line',
            paint: {
              'line-width': 3,
              'line-color': '#000000',
            },
          },
          'border-national-dispute',
        );

        /*
        map.addLayer(
          {
            id: 'circle-line-mini',
            source: 'circle-src-mini',
            type: 'line',
            paint: {
              'line-width': 3,
              'line-color': '#ff960c',
            },
          },
          'border-national-dispute',
        ); */
      };

      showCirc(coords);
    }

    return () => {
      if (mapLoaded) {
        map.off('movestart', onFlyStart);
        map.off('moveend', onFlyEnd);
      }
    };
  }, [mapLoaded, coords]);

  // render
  return (
    <div id="map-wrap">
      <div ref={mapContainer} className="mapContainer" />
    </div>
  );
}
